import React, { useMemo } from 'react'
import numeral from 'numeral'

interface InsightCard {
  icon: string
  iconColor: string
  title: string
  value?: string
  description?: string
  secondaryStats?: { label: string; value: string }[]
}

interface AIInsightCardsProps {
  reportData: Record<string, unknown>
  reportType?: string
}

const iconColors = {
  blue: 'tw-bg-blue-100 tw-text-blue-600',
  green: 'tw-bg-green-100 tw-text-green-600',
  orange: 'tw-bg-orange-100 tw-text-orange-600',
  purple: 'tw-bg-purple-100 tw-text-purple-600',
  red: 'tw-bg-red-100 tw-text-red-600',
  teal: 'tw-bg-teal-100 tw-text-teal-600',
  indigo: 'tw-bg-indigo-100 tw-text-indigo-600',
  pink: 'tw-bg-pink-100 tw-text-pink-600',
}

const InsightCardComponent: React.FC<{ card: InsightCard }> = React.memo(({ card }) => {
  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-5 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
      <div className="tw-flex tw-items-start tw-justify-between tw-mb-3">
        <div className={`tw-w-10 tw-h-10 tw-rounded-lg tw-flex tw-items-center tw-justify-center ${card.iconColor}`}>
          <span className={`fa ${card.icon} tw-text-lg`}></span>
        </div>
      </div>
      <h3 className="tw-text-sm tw-font-medium tw-text-gray-500 tw-mb-1">{card.title}</h3>
      {card.value && (
        <p className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-1">{card.value}</p>
      )}
      {card.description && (
        <p className="tw-text-sm tw-text-gray-500">{card.description}</p>
      )}
      {card.secondaryStats && card.secondaryStats.length > 0 && (
        <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-100">
          <div className="tw-grid tw-grid-cols-2 tw-gap-2">
            {card.secondaryStats.map((stat, i) => (
              <div key={i}>
                <p className="tw-text-xs tw-text-gray-400 tw-uppercase tw-tracking-wide">{stat.label}</p>
                <p className="tw-text-sm tw-font-semibold tw-text-gray-700">{stat.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
})

// Helper to format currency
const formatCurrency = (val: number) => numeral(val).format('$0,0')
const formatNumber = (val: number) => numeral(val).format('0,0')
const formatPercent = (val: number) => numeral(val / 100).format('0.0%')

// Extract stats from various data structures
const extractStats = (reportData: Record<string, unknown>, reportType?: string): Record<string, number> => {
  const data = reportData?.data as Record<string, unknown> || reportData || {}

  if (data.stats && typeof data.stats === 'object') {
    return data.stats as Record<string, number>
  }

  if (data.totals && typeof data.totals === 'object') {
    return data.totals as Record<string, number>
  }

  const trends = data.trends as Array<Record<string, number>> || []
  if (trends.length > 0) {
    const stats: Record<string, number> = {}

    trends.forEach(row => {
      Object.entries(row).forEach(([key, val]) => {
        if (typeof val === 'number' && key !== 'date' && !key.includes('margin')) {
          stats[`total_${key}`] = (stats[`total_${key}`] || 0) + val
        }
      })
    })

    if (stats.total_sales && stats.total_orders) {
      stats.avg_order = stats.total_sales / stats.total_orders
    }
    if (stats.total_profit && stats.total_sales) {
      stats.gross_margin = (stats.total_profit / stats.total_sales) * 100
    }

    return stats
  }

  const topLevelStats: Record<string, number> = {}
  const statKeys = ['total_sales', 'net_sales', 'sales', 'total_orders', 'orders', 'total_profit', 'profit',
                    'total_customers', 'customers', 'total_items_sold', 'items_sold', 'gross_margin',
                    'cost_of_goods_sold', 'avg_order', 'avg_price_per_item']

  statKeys.forEach(key => {
    if (typeof data[key] === 'number') {
      topLevelStats[key] = data[key] as number
    }
  })

  if (Object.keys(topLevelStats).length > 0) {
    return topLevelStats
  }

  return {}
}

// Check if this is an inventory report
const isInventoryReport = (reportType?: string, reportData?: Record<string, unknown>): boolean => {
  if (reportType?.startsWith('inventory') || reportType?.includes('inventory')) return true
  
  // Also check data keys
  const data = reportData?.data as Record<string, unknown> || reportData || {}
  if (data.inventoryProducts || data.inventoryVariants || data.inventoryBrands) return true
  
  // Check if docs array has inventory-like data (has quantity field)
  if (data.docs && Array.isArray(data.docs) && data.docs.length > 0) {
    const firstItem = data.docs[0] as Record<string, unknown>
    if (firstItem.quantity !== undefined || firstItem.inventory_value !== undefined) return true
  }
  
  return false
}

// Extract inventory stats from report data
const extractInventoryStats = (reportData: Record<string, unknown>, reportType?: string): Record<string, number> => {
  const data = reportData?.data as Record<string, unknown> || reportData || {}
  
  // Try to find inventory data from multiple possible locations
  let inventoryData: Array<Record<string, unknown>> = []
  
  // Primary location: data.docs (same as traffic reports)
  if (data.docs && Array.isArray(data.docs)) {
    inventoryData = data.docs as Array<Record<string, unknown>>
  }
  
  // Fallback: try other possible locations
  if (!inventoryData || inventoryData.length === 0) {
    if (reportType && data[reportType]) {
      inventoryData = data[reportType] as Array<Record<string, unknown>>
    } else if (data.inventoryProducts) {
      inventoryData = data.inventoryProducts as Array<Record<string, unknown>>
    } else if (data.inventoryVariants) {
      inventoryData = data.inventoryVariants as Array<Record<string, unknown>>
    } else if (data.inventoryBrands) {
      inventoryData = data.inventoryBrands as Array<Record<string, unknown>>
    } else if (Array.isArray(data) && data[0]?.quantity !== undefined) {
      inventoryData = data as unknown as Array<Record<string, unknown>>
    }
  }

  if (!inventoryData || !inventoryData.length) return {}

  const stats: Record<string, number> = {
    totalProducts: inventoryData.length,
    totalQuantity: 0,
    totalInventoryValue: 0,
    totalInventoryCost: 0,
    lowStockCount: 0,
    outOfStockCount: 0,
    totalSales: 0,
    totalProfit: 0,
  }

  inventoryData.forEach((item: Record<string, unknown>) => {
    const quantity = Number(item.quantity || item.total_quantity || 0)
    stats.totalQuantity += quantity
    stats.totalInventoryValue += Number(item.inventory_value || item.total_inventory_value || 0)
    stats.totalInventoryCost += Number(item.inventory_cost || item.total_inventory_cost || 0)
    stats.totalSales += Number(item.total_sales || 0)
    stats.totalProfit += Number(item.total_profit || 0)

    if (item.low_stock) stats.lowStockCount++
    if (quantity === 0) stats.outOfStockCount++
  })

  // Calculate averages
  if (inventoryData.length > 0) {
    stats.avgSellThroughRate = inventoryData.reduce((sum, item) =>
      sum + Number(item.sell_through_rate || 0), 0) / inventoryData.length
    stats.avgDaysOfStock = inventoryData.reduce((sum, item) =>
      sum + Number(item.days_of_stock || 0), 0) / inventoryData.length
  }

  return stats
}

// Generate inventory insight cards
const generateInventoryInsightCards = (reportData: Record<string, unknown>, reportType?: string): InsightCard[] => {
  const cards: InsightCard[] = []
  const stats = extractInventoryStats(reportData, reportType)

  if (Object.keys(stats).length === 0) return []
  
  // Determine if this is a brands report
  const data = reportData?.data as Record<string, unknown> || reportData || {}
  const isBrands = reportType === 'inventoryBrands' || !!data.inventoryBrands

  // Total Inventory Value Card
  if (stats.totalInventoryValue > 0) {
    cards.push({
      icon: 'fa-warehouse',
      iconColor: iconColors.blue,
      title: 'Total Inventory Value',
      value: formatCurrency(stats.totalInventoryValue),
      description: `${formatNumber(stats.totalQuantity)} units in stock`,
      secondaryStats: stats.totalInventoryCost ? [
        { label: 'Cost Value', value: formatCurrency(stats.totalInventoryCost) },
      ] : undefined,
    })
  }

  // Stock Health Card
  if (stats.lowStockCount > 0 || stats.outOfStockCount > 0) {
    const healthStatus = stats.outOfStockCount > 5 ? 'Critical' : stats.lowStockCount > 10 ? 'Warning' : 'Good'
    cards.push({
      icon: stats.outOfStockCount > 0 ? 'fa-exclamation-triangle' : 'fa-box-open',
      iconColor: stats.outOfStockCount > 5 ? iconColors.red : stats.lowStockCount > 10 ? iconColors.orange : iconColors.green,
      title: 'Stock Health',
      value: healthStatus,
      description: `${formatNumber(stats.lowStockCount)} low stock items`,
      secondaryStats: [
        { label: 'Low Stock', value: formatNumber(stats.lowStockCount) },
        { label: 'Out of Stock', value: formatNumber(stats.outOfStockCount) },
      ],
    })
  }

  // Performance Card (if sales data available)
  if (stats.totalSales > 0) {
    cards.push({
      icon: 'fa-chart-line',
      iconColor: iconColors.green,
      title: 'Inventory Performance',
      value: formatCurrency(stats.totalSales),
      description: 'Total sales value',
      secondaryStats: stats.totalProfit ? [
        { label: 'Profit', value: formatCurrency(stats.totalProfit) },
      ] : undefined,
    })
  }

  // Products/Items Card
  if (stats.totalProducts > 0) {
    const avgDays = stats.avgDaysOfStock ? Math.round(stats.avgDaysOfStock) : null
    cards.push({
      icon: 'fa-boxes-stacked',
      iconColor: iconColors.purple,
      title: isBrands ? 'Total Brands' : 'Total Products',
      value: formatNumber(stats.totalProducts),
      description: avgDays ? `Avg ${avgDays} days of stock` : undefined,
      secondaryStats: stats.avgSellThroughRate ? [
        { label: 'Avg Sell-Through', value: formatPercent(stats.avgSellThroughRate * 100) },
      ] : undefined,
    })
  }

  return cards.slice(0, 4)
}

// Generate insight cards from report data
const generateInsightCards = (reportData: Record<string, unknown>, reportType?: string): InsightCard[] => {
  // Handle inventory reports separately
  if (isInventoryReport(reportType, reportData)) {
    return generateInventoryInsightCards(reportData, reportType)
  }

  const cards: InsightCard[] = []
  const stats = extractStats(reportData, reportType)
  const data = reportData?.data as Record<string, unknown> || reportData || {}

  const sales = stats.total_sales || stats.net_sales || stats.sales
  const orders = stats.total_orders || stats.orders
  const itemsSold = stats.total_items_sold || stats.items_sold
  const profit = stats.total_profit || stats.profit
  const margin = stats.gross_margin
  const cogs = stats.cost_of_goods_sold || stats.total_cost_of_goods_sold
  const customers = stats.total_customers || stats.customers
  const avgOrder = stats.avg_order
  const avgPrice = stats.avg_price_per_item || stats.total_avg_price_per_item

  // Sales Card
  if (sales) {
    const secondaryStats: { label: string; value: string }[] = []
    if (itemsSold) secondaryStats.push({ label: 'Items Sold', value: formatNumber(itemsSold) })
    if (avgOrder) secondaryStats.push({ label: 'Avg Order', value: formatCurrency(avgOrder) })

    cards.push({
      icon: 'fa-dollar-sign',
      iconColor: iconColors.blue,
      title: 'Total Sales',
      value: formatCurrency(sales),
      description: orders ? `${formatNumber(orders)} orders` : undefined,
      secondaryStats: secondaryStats.length > 0 ? secondaryStats : undefined,
    })
  }

  // Profitability Card
  if (profit !== undefined || margin !== undefined) {
    const secondaryStats: { label: string; value: string }[] = []
    if (margin) secondaryStats.push({ label: 'Margin', value: formatPercent(margin) })
    if (cogs) secondaryStats.push({ label: 'COGS', value: formatCurrency(cogs) })

    cards.push({
      icon: 'fa-chart-line',
      iconColor: iconColors.green,
      title: 'Profitability',
      value: profit ? formatCurrency(profit) : (margin ? formatPercent(margin) : undefined),
      description: profit && margin ? `${formatPercent(margin)} gross margin` : undefined,
      secondaryStats: secondaryStats.length > 0 ? secondaryStats : undefined,
    })
  }

  // Customers Card
  if (customers) {
    cards.push({
      icon: 'fa-users',
      iconColor: iconColors.purple,
      title: 'Customers',
      value: formatNumber(customers),
      description: 'Unique customers served',
    })
  }

  // Orders Card (if sales card wasn't created)
  if (orders && !sales) {
    cards.push({
      icon: 'fa-shopping-cart',
      iconColor: iconColors.orange,
      title: 'Total Orders',
      value: formatNumber(orders),
      description: itemsSold ? `${formatNumber(itemsSold)} items sold` : undefined,
    })
  }

  // Items Sold Card (if not in sales card)
  if (itemsSold && cards.length < 4 && !cards[0]?.secondaryStats?.some(s => s.label === 'Items Sold')) {
    cards.push({
      icon: 'fa-box',
      iconColor: iconColors.teal,
      title: 'Items Sold',
      value: formatNumber(itemsSold),
      description: avgPrice ? `Avg ${formatCurrency(avgPrice)} per item` : undefined,
    })
  }

  // Year Comparison specific cards
  const yearComparisons = data.yearComparisons as Record<string, { stats?: Record<string, number>, trends?: Array<Record<string, number>> }> || {}
  const years = Object.keys(yearComparisons).sort()

  if (years.length >= 2) {
    const latestYear = years[years.length - 1]
    const previousYear = years[years.length - 2]
    const latestYearData = yearComparisons[latestYear] || {}
    const prevYearData = yearComparisons[previousYear] || {}

    let latestSales = latestYearData.stats?.total_sales
    let prevSales = prevYearData.stats?.total_sales

    if (!latestSales && latestYearData.trends) {
      latestSales = latestYearData.trends.reduce((sum, row) => sum + (row.sales || 0), 0)
    }
    if (!prevSales && prevYearData.trends) {
      prevSales = prevYearData.trends.reduce((sum, row) => sum + (row.sales || 0), 0)
    }

    if (latestSales && prevSales) {
      const growth = ((latestSales - prevSales) / prevSales) * 100
      const growthFormatted = growth > 0 ? `+${growth.toFixed(1)}%` : `${growth.toFixed(1)}%`

      cards.push({
        icon: growth > 0 ? 'fa-arrow-trend-up' : 'fa-arrow-trend-down',
        iconColor: growth > 0 ? iconColors.green : iconColors.red,
        title: 'Year over Year',
        value: growthFormatted,
        description: `${latestYear} vs ${previousYear}`,
        secondaryStats: [
          { label: latestYear, value: formatCurrency(latestSales) },
          { label: previousYear, value: formatCurrency(prevSales) },
        ],
      })
    }
  }

  return cards.slice(0, 4)
}

/** Whether this report produces any summary cards (lets callers hide an empty tab). */
export const hasInsightCards = (reportData: any, reportType?: string | null): boolean =>
  !!reportData && generateInsightCards(reportData, reportType as any).length > 0

const AIInsightCards: React.FC<AIInsightCardsProps> = React.memo(({ reportData, reportType }) => {
  const cards = useMemo(() => {
    if (!reportData) return []
    return generateInsightCards(reportData, reportType)
  }, [reportData, reportType])

  if (cards.length === 0) return null

  return (
    <div className={`tw-grid tw-gap-4 tw-my-4 ${
      cards.length === 1 ? 'tw-grid-cols-1' :
      cards.length === 2 ? 'tw-grid-cols-1 sm:tw-grid-cols-2' :
      cards.length === 3 ? 'tw-grid-cols-1 sm:tw-grid-cols-3' :
      'tw-grid-cols-2 lg:tw-grid-cols-4'
    }`}>
      {cards.map((card, index) => (
        <InsightCardComponent key={index} card={card} />
      ))}
    </div>
  )
})

export default AIInsightCards
